import { useCallback, useEffect, useRef, useState } from "react";
import {
  CircleUserRound,
  Copy,
  FileOutput,
  LogOut,
  PanelLeftOpen,
  Unlink
} from "lucide-react";
import {
  confirmCheckout,
  logoutAccount,
  probeAccount,
  updateAccountSettings,
  type DictaAccount
} from "./app/account";
import { DEMO_MATTER, DEMO_TRANSCRIPT } from "./app/demo";
import { recordingAuthorityStatus } from "./domain";
import type {
  AppView,
  CaptureState,
  MatterDraft,
  TranscriptRow,
  TranscriptSession,
  TranscriptDetail,
  TranscriptSaveState,
  TranscriptSummary
} from "./app/ui-types";
import {
  ACTIVE_CAPTURE_CHANGE_EVENT,
  clearActiveCapture,
  hasStoredMatterDraft,
  readActiveCapture,
  readMatterDraft,
  writeActiveCapture,
  writeMatterDraft
} from "./app/storage";
import { blankMatterDraft, seedDefaultSpeaker } from "./app/blank-matter";
import { FEATURE_FLAGS } from "./app/feature-flags";
import { CommandPalette } from "./components/CommandPalette";
import { AccountPanel, type AccountTab } from "./components/AccountPanel";
import { ExportPanel } from "./components/ExportPanel";
import { LiveCapture } from "./components/LiveCapture";
import { RecordingSettings } from "./components/RecordingSettings";
import { RecoveryWorkspace } from "./components/RecoveryWorkspace";
import { ReviewWorkspace } from "./components/ReviewWorkspace";
import { SignIn } from "./components/SignIn";
import { LegalPage, type LegalDocument } from "./components/LegalPage";
import { SharedTranscriptPage } from "./components/SharedTranscriptPage";
import { apiUrl, responseError } from "./app/api";
import type { LiveShare } from "./hooks/useLiveSession";
import { TranscriptLibrary } from "./components/TranscriptLibrary";
import { SpeakerNameDialog } from "./components/SpeakerNameDialog";
import {
  createTranscript,
  finaliseTranscript,
  loadCompositeTranscript,
  loadTranscript,
  saveTranscript
} from "./app/transcripts";
import { sendEntryTelemetry } from "./app/telemetry";
import { createRecordingBuffer, IndexedDbRecordingBuffer } from "./services";
import { captureSupportFailure } from "./app/audio-processing";
import { discardUnfinishedCapture } from "./app/recording-recovery";

const REVIEW_SESSION_KEY = "dicta-review-session"; // legacy key; cleared during migration
const MULTI_SESSION_ENABLED =
  import.meta.env.VITE_MULTI_SESSION_TRANSCRIPTS !== "false";

function AuthenticatedApp() {
  const [homepageOpen, setHomepageOpen] = useState(
    () =>
      window.location.pathname === "/" &&
      !new URLSearchParams(window.location.search).has("checkout")
  );
  const [sandbox, setSandbox] = useState(
    () =>
      sessionStorage.getItem("dicta-sandbox") === "true" ||
      window.location.pathname === "/try"
  );
  const [signInInitialMode, setSignInInitialMode] = useState<
    "signin" | "signup"
  >("signin");
  const [signInInitialOpen, setSignInInitialOpen] = useState(false);
  const [hadStoredDraft] = useState(hasStoredMatterDraft);
  const [authMode, setAuthMode] = useState<"loading" | "local" | "remote">("loading");
  const [account, setAccount] = useState<DictaAccount | null>(null);
  const [localUser, setLocalUser] = useState(
    () => sessionStorage.getItem("dicta-demo-user") ?? ""
  );
  const [accountPanelOpen, setAccountPanelOpen] = useState(false);
  const [accountPanelTab, setAccountPanelTab] = useState<AccountTab>("profile");
  const [checkoutNotice, setCheckoutNotice] = useState<string | null>(null);
  const [speakerNamePromptOpen, setSpeakerNamePromptOpen] = useState(false);
  const [speakerNameBusy, setSpeakerNameBusy] = useState(false);
  const [speakerNameError, setSpeakerNameError] = useState<string | null>(null);
  const user = account?.user.email ?? localUser;
  const [transcriptSession, setTranscriptSession] = useState<TranscriptSession | null>(null);
  const [view, setView] = useState<AppView>(() =>
    sandbox || !MULTI_SESSION_ENABLED ? "live" : "library"
  );
  const [matter, setMatter] = useState<MatterDraft>(() =>
    sandbox ? DEMO_MATTER : readMatterDraft(blankMatterDraft())
  );
  const [transcriptRows, setTranscriptRows] = useState<TranscriptRow[]>(
    transcriptSession ? [] : sandbox ? DEMO_TRANSCRIPT : []
  );
  const [commandOpen, setCommandOpen] = useState(false);
  const [legalDocument, setLegalDocument] = useState<LegalDocument | null>(() =>
    window.location.pathname === "/terms"
      ? "terms"
      : window.location.pathname === "/privacy"
        ? "privacy"
        : window.location.pathname === "/faq"
          ? "faq"
          : null
  );
  const [captureState, setCaptureState] = useState<CaptureState>("ready");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [liveShare, setLiveShare] = useState<LiveShare | null>(null);
  const [liveShareBusy, setLiveShareBusy] = useState(false);
  const [selectedTranscript, setSelectedTranscript] = useState<TranscriptDetail | null>(null);
  const [saveState, setSaveState] = useState<TranscriptSaveState>("idle");
  const [libraryRefresh, setLibraryRefresh] = useState(0);
  const [pendingLocalRecovery, setPendingLocalRecovery] = useState(readActiveCapture);
  const [deviceRecoveryMatter, setDeviceRecoveryMatter] = useState<MatterDraft | null>(null);
  const lastServerMatter = useRef("");
  const restoreAttempted = useRef(false);
  const authority = recordingAuthorityStatus(matter);
  const captureLocked =
    captureState === "listening" ||
    captureState === "processing";

  useEffect(() => {
    let active = true;
    void probeAccount().then((probe) => {
      if (!active) return;
      setAuthMode(probe.mode);
      setAccount(probe.account);
      if (probe.account) {
        if (sessionStorage.getItem("dicta-sample-signup-pending") === "true") {
          sessionStorage.removeItem("dicta-sample-signup-pending");
          sendEntryTelemetry("client.sample.account_converted", "sample");
        }
        setLocalUser("");
        // Seed the user's preferred jurisdiction into a fresh matter draft.
        const defaultPack = probe.account.settings.defaultJurisdictionPackId;
        if (!hadStoredDraft && !sandbox) {
          setMatter(blankMatterDraft(defaultPack, probe.account.user.name));
        }
      } else if (probe.mode === "local" && !hadStoredDraft && !sandbox) {
        setMatter(blankMatterDraft());
        setTranscriptRows([]);
      }
    });
    return () => {
      active = false;
    };
  // Account identity and sample mode deliberately coexist. Probing must happen
  // once: entering /try must never sign a user out or cancel the sandbox.
  }, [hadStoredDraft]);

  // Stripe Checkout returns to /transcripts?checkout=success&session_id=... (or
  // checkout=cancelled). Confirm the session server-side rather than waiting
  // for the webhook, then land the user on the billing tab.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get("checkout");
    if (!checkout) return;
    const sessionId = params.get("session_id");
    setHomepageOpen(false);
    window.history.replaceState({}, "", "/transcripts");
    if (checkout === "cancelled") {
      setCheckoutNotice("Checkout was cancelled — no charge was made.");
      return;
    }
    if (checkout !== "success" || !sessionId) return;
    setCheckoutNotice("Finalising your subscription…");
    void confirmCheckout(sessionId)
      .then((nextAccount) => {
        setAccount(nextAccount);
        setCheckoutNotice(null);
        setAccountPanelTab("billing");
        setAccountPanelOpen(true);
      })
      .catch((cause: unknown) => {
        setCheckoutNotice(
          cause instanceof Error
            ? cause.message
            : "The subscription could not be confirmed yet. It will activate as soon as Stripe notifies us."
        );
      });
  }, []);

  useEffect(() => {
    const handleUsageUpdate = (event: Event) => {
      const detail = (event as CustomEvent<DictaAccount["usage"]>).detail;
      if (!detail) return;
      setAccount((current) =>
        current ? { ...current, usage: detail } : current
      );
    };
    window.addEventListener("dicta:usage-updated", handleUsageUpdate);
    return () =>
      window.removeEventListener("dicta:usage-updated", handleUsageUpdate);
  }, []);

  useEffect(() => {
    if (!user || sandbox) return;
    writeMatterDraft(matter);
    if (!selectedTranscript || selectedTranscript.status === "finalised") return;
    const signature = JSON.stringify(matter);
    if (signature === lastServerMatter.current) return;
    setSaveState("saving");
    const timer = window.setTimeout(() => {
      void saveTranscript(selectedTranscript, matter)
        .then((detail) => {
          setSelectedTranscript(detail);
          lastServerMatter.current = signature;
          setSavedAt(Date.now());
          setSaveState("saved");
          setLibraryRefresh((current) => current + 1);
        })
        .catch((cause: unknown) => {
          setSaveState(
            cause instanceof Error && cause.message.toLowerCase().includes("another device")
              ? "conflict"
              : "error"
          );
          setCheckoutNotice(
            cause instanceof Error ? cause.message : "Transcript details could not be saved."
          );
        });
    }, 450);
    return () => window.clearTimeout(timer);
  }, [matter, sandbox, selectedTranscript, user]);

  useEffect(() => {
    if (sandbox) {
      sessionStorage.setItem("dicta-sandbox", "true");
    } else {
      sessionStorage.removeItem("dicta-sandbox");
    }
    if (window.location.pathname === "/try") {
      window.history.replaceState({}, "", "/");
    }
  }, [sandbox]);

  const sampleCompletionReported = useRef(false);
  useEffect(() => {
    if (!sandbox || captureState !== "review" || sampleCompletionReported.current) return;
    sampleCompletionReported.current = true;
    sendEntryTelemetry("client.sample.completed", "sample");
  }, [captureState, sandbox]);

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if (
        !captureLocked &&
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === "k"
      ) {
        event.preventDefault();
        setCommandOpen((current) => !current);
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, [captureLocked]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    document.getElementById("main-content")?.focus({ preventScroll: true });
  }, [view]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [user]);

  useEffect(() => {
    if (!liveShare?.expiresAt) return;
    const remaining = new Date(liveShare.expiresAt).valueOf() - Date.now();
    if (remaining <= 0) {
      setLiveShare(null);
      return;
    }
    const timer = window.setTimeout(() => setLiveShare(null), remaining);
    return () => window.clearTimeout(timer);
  }, [liveShare?.expiresAt]);

  useEffect(() => {
    const syncLegalRoute = () => {
      const path = window.location.pathname;
      setLegalDocument(
        path === "/terms"
          ? "terms"
          : path === "/privacy"
            ? "privacy"
            : path === "/faq"
              ? "faq"
              : null
      );
    };
    window.addEventListener("popstate", syncLegalRoute);
    return () => window.removeEventListener("popstate", syncLegalRoute);
  }, []);

  const openTranscriptLibrary = () => {
    setHomepageOpen(false);
    setView(MULTI_SESSION_ENABLED ? "library" : "live");
    if (window.location.pathname !== "/transcripts") {
      window.history.pushState({}, "", "/transcripts");
    }
  };

  const authenticate = (email: string) => {
    sessionStorage.setItem("dicta-demo-user", email);
    setLocalUser(email);
    setHomepageOpen(true);
    setView("library");
    if (window.location.pathname !== "/") {
      window.history.replaceState({}, "", "/");
    }
  };

  const enterSandbox = () => {
    sessionStorage.removeItem(REVIEW_SESSION_KEY);
    setSandbox(true);
    setMatter(DEMO_MATTER);
    setTranscriptRows(DEMO_TRANSCRIPT);
    setTranscriptSession(null);
    setCaptureState("ready");
    setSettingsOpen(false);
    setView("live");
    sampleCompletionReported.current = false;
  };

  const exitSandbox = (target: "home" | "signin" | "signup") => {
    if (target === "signup") {
      sendEntryTelemetry("client.sample.signup_selected", "sample");
      sessionStorage.setItem("dicta-sample-signup-pending", "true");
    }
    sessionStorage.removeItem("dicta-sandbox");
    sessionStorage.removeItem(REVIEW_SESSION_KEY);
    setSandbox(false);
    setSignInInitialMode(target === "signup" ? "signup" : "signin");
    setSignInInitialOpen(target !== "home");
    setTranscriptSession(null);
    setTranscriptRows([]);
    setMatter(blankMatterDraft());
    setCaptureState("ready");
    setSettingsOpen(false);
    setView("live");
    setHomepageOpen(true);
    window.history.pushState({}, "", "/");
  };

  const signOut = async () => {
    if (captureLocked) return;
    if (authMode === "remote") {
      await logoutAccount().catch(() => undefined);
    }
    sessionStorage.removeItem("dicta-demo-user");
    sessionStorage.removeItem(REVIEW_SESSION_KEY);
    setTranscriptSession(null);
    setTranscriptRows([]);
    setMatter(blankMatterDraft());
    setSelectedTranscript(null);
    setView(MULTI_SESSION_ENABLED ? "library" : "live");
    setAccount(null);
    setLocalUser("");
    setAccountPanelOpen(false);
    setHomepageOpen(true);
    window.history.pushState({}, "", "/");
  };

  const navigate = (nextView: AppView) => {
    if (captureLocked && nextView !== "live") return;
    if (nextView === "review" && transcriptRows.length === 0) return;
    setView(nextView === "matter" ? "live" : nextView);
    if (nextView === "library") {
      setHomepageOpen(false);
      window.history.pushState({}, "", "/transcripts");
    } else if (selectedTranscript && nextView !== "matter") {
      const route = nextView === "live" ? "record" : nextView;
      window.history.pushState(
        {},
        "",
        `/transcripts/${encodeURIComponent(selectedTranscript.id)}/${route}`
      );
    }
  };

  const sessionFromDetail = (detail: TranscriptDetail): TranscriptSession | null => {
    const latest = detail.sessions.at(-1);
    if (!latest?.transcriptId || !latest.versionId || !latest.versionNumber || !latest.versionState) {
      return null;
    }
    return {
      matterId: detail.id,
      sessionOrdinal: latest.ordinal,
      recordingId: latest.id,
      transcriptId: latest.transcriptId,
      versionId: latest.versionId,
      versionNumber: latest.versionNumber,
      state: latest.versionState,
      recordedAtEpochMs: Date.parse(latest.startedAt ?? latest.createdAt),
      sourceAudio: latest.sourceAudio
    };
  };

  const openTranscriptById = useCallback(
    async (
      id: string,
      target?: "record" | "review" | "export" | "recover",
      historyMode: "push" | "replace" | "none" = "push"
    ) => {
      try {
        const [detail, composite] = await Promise.all([
          loadTranscript(id),
          loadCompositeTranscript(id)
        ]);
        setSelectedTranscript(detail);
        const nextMatter = seedDefaultSpeaker(detail.matter, account?.user.name);
        setMatter(nextMatter);
        lastServerMatter.current = JSON.stringify(detail.matter);
        setTranscriptRows(composite.rows);
        setTranscriptSession(sessionFromDetail(detail));
        setSaveState("saved");
        const next = target ?? (detail.status === "finalised" ? "review" : "record");
        setView(next === "record" ? "live" : next === "recover" ? "recovery" : next);
        const nextPath = `/transcripts/${encodeURIComponent(id)}/${next}`;
        if (historyMode === "replace") {
          window.history.replaceState({}, "", nextPath);
        } else if (historyMode === "push") {
          window.history.pushState({}, "", nextPath);
        }
      } catch (cause) {
        setCheckoutNotice(
          cause instanceof Error ? cause.message : "Transcript could not be opened."
        );
        setView("library");
      }
    },
    [account?.user.name]
  );

  useEffect(() => {
    const sync = () => setPendingLocalRecovery(readActiveCapture());
    window.addEventListener(ACTIVE_CAPTURE_CHANGE_EVENT, sync);
    return () => window.removeEventListener(ACTIVE_CAPTURE_CHANGE_EVENT, sync);
  }, []);

  useEffect(() => {
    if (sandbox || !user || !pendingLocalRecovery) return;
    let cancelled = false;
    void (async () => {
      const metadata = pendingLocalRecovery;
      const buffer = await createRecordingBuffer();
      try {
        const [source, analysis, response] = await Promise.all([
          buffer.listChunks(metadata.recordingId),
          buffer.listChunks(`analysis:${metadata.recordingId}`),
          fetch(apiUrl(`/api/recordings/${encodeURIComponent(metadata.recordingId)}`), {
            credentials: "same-origin"
          })
        ]);
        if (cancelled) return;
        const recordingView = response.ok
          ? ((await response.json()) as {
              recording?: { status?: string; matterId?: string };
              manifest?: unknown;
            })
          : null;
        const resolvedMatterId = metadata.matterId ?? recordingView?.recording?.matterId;
        if (resolvedMatterId && resolvedMatterId !== metadata.matterId) {
          writeActiveCapture({ ...metadata, matterId: resolvedMatterId });
        }
        const serverReady =
          recordingView?.recording?.status === "review" ||
          recordingView?.recording?.status === "done" ||
          recordingView?.manifest !== undefined;
        if (serverReady && resolvedMatterId) {
          clearActiveCapture();
          setCheckoutNotice("The interrupted recording was already prepared. Opening Review.");
          await openTranscriptById(resolvedMatterId, "review", "replace");
          return;
        }
        const localEvidenceVerifiedEmpty =
          buffer instanceof IndexedDbRecordingBuffer &&
          source.length === 0 &&
          analysis.length === 0;
        if (localEvidenceVerifiedEmpty && (response.status === 404 || response.status === 410)) {
          clearActiveCapture();
          setCheckoutNotice(
            "A stale device recovery marker was cleared. No recording evidence or active server session was found."
          );
        }
      } catch {
        // A transient local or server failure must not remove a recovery blocker.
      } finally {
        buffer.close();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [openTranscriptById, pendingLocalRecovery, sandbox, user]);

  const createTranscriptForSpeaker = async (defaultSpeakerName?: string | null) => {
    const unsupported = import.meta.env.MODE === "test" ? null : captureSupportFailure();
    if (unsupported) {
      setCheckoutNotice(unsupported);
      return;
    }
    const draft = blankMatterDraft(
      account?.settings.defaultJurisdictionPackId,
      defaultSpeakerName
    );
    try {
      const detail = await createTranscript(draft);
      setSelectedTranscript(detail);
      setMatter(detail.matter);
      lastServerMatter.current = JSON.stringify(detail.matter);
      setTranscriptRows([]);
      setTranscriptSession(null);
      setCaptureState("ready");
      setSettingsOpen(!FEATURE_FLAGS.quickStart);
      setSaveState("saved");
      setView("live");
      setLibraryRefresh((current) => current + 1);
      window.history.pushState({}, "", `/transcripts/${encodeURIComponent(detail.id)}/record`);
    } catch (cause) {
      setCheckoutNotice(
        cause instanceof Error ? cause.message : "A new transcript could not be created."
      );
    }
  };

  const discardPendingRecovery = useCallback(async (): Promise<boolean> => {
    const recovery = readActiveCapture() ?? pendingLocalRecovery;
    if (!recovery) return true;
    if (
      !window.confirm(
        "Discard unfinished capture?\n\nThis permanently deletes the unfinished transcript and both device audio copies. This cannot be undone."
      )
    ) {
      return false;
    }
    const buffer = await createRecordingBuffer();
    try {
      await discardUnfinishedCapture(recovery, buffer);
      setPendingLocalRecovery(null);
      setDeviceRecoveryMatter(null);
      setLibraryRefresh((current) => current + 1);
      setCheckoutNotice("The unfinished capture was discarded. This device is ready to record.");
      return true;
    } catch (cause) {
      setCheckoutNotice(
        cause instanceof Error
          ? cause.message
          : "The unfinished capture could not be discarded."
      );
      return false;
    } finally {
      buffer.close();
    }
  }, [pendingLocalRecovery]);

  const createNewTranscript = async () => {
    if (captureLocked) return;
    const pendingRecovery = !sandbox ? pendingLocalRecovery : null;
    if (pendingRecovery && !(await discardPendingRecovery())) return;
    if (sandbox) {
      startNewRecording();
      return;
    }
    if (account && !account.user.name?.trim()) {
      setSpeakerNameError(null);
      setSpeakerNamePromptOpen(true);
      return;
    }
    await createTranscriptForSpeaker(account?.user.name);
  };

  const clearDeletedTranscriptRecovery = useCallback((transcript: TranscriptSummary) => {
    const recovery = readActiveCapture();
    if (recovery?.matterId !== transcript.id) return;

    clearActiveCapture();
    void (async () => {
      const buffer = await createRecordingBuffer();
      try {
        await Promise.all([
          buffer.clearRecording(recovery.recordingId),
          buffer.clearRecording(`analysis:${recovery.recordingId}`)
        ]);
      } finally {
        buffer.close();
      }
    })().catch(() => undefined);
  }, []);

  const saveDefaultSpeakerName = async (name: string) => {
    if (!account || speakerNameBusy) return;
    setSpeakerNameBusy(true);
    setSpeakerNameError(null);
    try {
      const nextAccount = await updateAccountSettings({ name });
      setAccount(nextAccount);
      setSpeakerNamePromptOpen(false);
      await createTranscriptForSpeaker(nextAccount.user.name);
    } catch (cause) {
      setSpeakerNameError(
        cause instanceof Error ? cause.message : "Your display name could not be saved."
      );
    } finally {
      setSpeakerNameBusy(false);
    }
  };

  const refreshComposite = useCallback(async () => {
    if (!selectedTranscript) return [];
    const composite = await loadCompositeTranscript(selectedTranscript.id);
    setTranscriptRows(composite.rows);
    return composite.rows;
  }, [selectedTranscript]);

  useEffect(() => {
    if (!user || sandbox || restoreAttempted.current) return;
    restoreAttempted.current = true;
    if (!MULTI_SESSION_ENABLED) {
      setView("live");
      return;
    }
    const match = window.location.pathname.match(
      /^\/transcripts\/([^/]+)\/(record|review|export|recover)$/
    );
    if (match) {
      void openTranscriptById(
        decodeURIComponent(match[1]),
        match[2] as "record" | "review" | "export" | "recover",
        "replace"
      );
    } else {
      setView("library");
      if (window.location.pathname !== "/") {
        window.history.replaceState({}, "", "/transcripts");
      }
    }
  }, [openTranscriptById, sandbox, user]);

  useEffect(() => {
    const syncAppRoute = () => {
      if (!user || sandbox || legalDocument) return;
      const path = window.location.pathname;
      if (path === "/" || path === "/transcripts") {
        setHomepageOpen(path === "/");
        setView("library");
        return;
      }
      setHomepageOpen(false);
      const match = path.match(/^\/transcripts\/([^/]+)\/(record|review|export|recover)$/);
      if (match) {
        void openTranscriptById(
          decodeURIComponent(match[1]),
          match[2] as "record" | "review" | "export" | "recover",
          "none"
        );
      }
    };
    window.addEventListener("popstate", syncAppRoute);
    return () => window.removeEventListener("popstate", syncAppRoute);
  }, [legalDocument, openTranscriptById, sandbox, user]);

  /**
   * Closes out the current transcription and opens a fresh one: the finished
   * matter is already persisted server-side, so this only clears local
   * session state and seeds a new draft that keeps standing matter context
   * while clearing the title, speakers, vocabulary, authority attestations,
   * and any captured transcript.
   */
  const startNewRecording = () => {
    if (captureLocked) return;
    sessionStorage.removeItem(REVIEW_SESSION_KEY);
    setTranscriptSession(null);
    setTranscriptRows([]);
    setLiveShare(null);
    setCaptureState("ready");
    setMatter((current) => ({
      ...current,
      title: "",
      reference: "",
      participants:
        current.participants.length > 0
          ? current.participants
          : account?.user.name?.trim()
            ? [{ name: account.user.name.trim(), role: "Recorder" }]
            : [],
      glossary: [],
      authorityReference: "",
      date: new Date().toISOString().slice(0, 10),
      authority: {
        ...current.authority,
        userAttestation: false,
        participantNotice: false,
        participantConsent: false,
        confidentialityReviewed: false,
        courtApproval: false
      }
    }));
    setView("live");
    setSettingsOpen(true);
  };

  const stopLiveShare = async () => {
    if (!transcriptSession?.recordingId || !liveShare) return;
    setLiveShareBusy(true);
    try {
      const response = await fetch(
        apiUrl(
          `/api/recordings/${encodeURIComponent(
            transcriptSession.recordingId
          )}/live-share`
        ),
        { method: "DELETE", credentials: "same-origin" }
      );
      if (!response.ok) {
        throw await responseError(
          response,
          "The live transcript link could not be revoked."
        );
      }
      setLiveShare(null);
    } catch (cause) {
      setCheckoutNotice(
        cause instanceof Error
          ? cause.message
          : "The live transcript link could not be revoked."
      );
    } finally {
      setLiveShareBusy(false);
    }
  };

  const updateTranscriptSession = (next: TranscriptSession) => {
    setTranscriptSession((current) => ({
      ...next,
      ...(next.audioUrl || !current?.audioUrl ? {} : { audioUrl: current.audioUrl })
    }));
  };

  if (legalDocument) {
    return (
      <LegalPage
        document={legalDocument}
        onBack={() => {
          window.history.pushState({}, "", "/");
          setHomepageOpen(true);
          setLegalDocument(null);
        }}
      />
    );
  }

  if (authMode === "loading" && !sandbox) {
    return (
      <main className="auth-loading" aria-live="polite">
        <span className="record-dot record-dot--active" aria-hidden="true" />
        Checking secure access…
      </main>
    );
  }

  if (!sandbox && (homepageOpen || !user)) {
    return (
      <SignIn
        authenticated={Boolean(user)}
        initialMode={signInInitialMode}
        initialOpen={signInInitialOpen}
        onTrySample={enterSandbox}
        onOpenWorkspace={openTranscriptLibrary}
        onSignOut={() => void signOut()}
        remote={authMode === "remote"}
        onAuthenticated={authenticate}
      />
    );
  }

  return (
    <div className="app-shell">
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>
      <header className="app-header">
        <button
          className="brand"
          type="button"
          onClick={() => navigate(MULTI_SESSION_ENABLED ? "library" : "live")}
          disabled={captureLocked && view !== "live"}
          aria-label="Open transcript library"
        >
          <span className="brand-wordmark">dicta</span>
          <span className="beta-badge">Beta</span>
        </button>
        {view !== "library" && view !== "recovery" && <nav className="workflow-nav" aria-label="Transcription stages">
          <button
            type="button"
            className={view === "live" ? "is-active" : ""}
            aria-current={view === "live" ? "step" : undefined}
            onClick={() => navigate("live")}
          >
            Record
          </button>
          <button
            type="button"
            className={view === "review" ? "is-active" : ""}
            aria-current={view === "review" ? "step" : undefined}
            onClick={() => navigate("review")}
            disabled={captureLocked || transcriptRows.length === 0}
            title={
              transcriptRows.length === 0
                ? "Stop a recording before review"
                : undefined
            }
          >
            Review
          </button>
          <button
            type="button"
            className={view === "export" ? "is-active" : ""}
            aria-current={view === "export" ? "step" : undefined}
            onClick={() => navigate("export")}
            disabled={
              captureLocked ||
              (view !== "export" &&
                selectedTranscript?.status !== "finalised" &&
                transcriptSession?.state !== "frozen")
            }
            title={
              selectedTranscript?.status !== "finalised" && transcriptSession?.state !== "frozen"
                ? "Finalise the reviewed transcript before export"
                : undefined
            }
          >
            Export
          </button>
        </nav>}
        <div className="header-actions">
          {view === "export" && (
            <button
              type="button"
              className="button button--text"
              onClick={() => navigate("review")}
            >
              <FileOutput size={16} aria-hidden="true" />
              Transcript
            </button>
          )}
          {!sandbox && (
            <button
              className="user-menu"
              type="button"
              onClick={() =>
                account ? setAccountPanelOpen(true) : void signOut()
              }
              title={
                captureLocked
                  ? "Stop recording before opening account controls"
                  : account
                    ? "Open account"
                    : "Sign out"
              }
              disabled={captureLocked}
              aria-label={account ? `Open account for ${user}` : `Sign out ${user}`}
            >
              <CircleUserRound size={18} aria-hidden="true" />
              {account ? null : <LogOut size={14} aria-hidden="true" />}
            </button>
          )}
        </div>
      </header>

      {checkoutNotice && (
        <div className="sandbox-banner" role="status">
          <span>{checkoutNotice}</span>
          <span className="sandbox-banner__actions">
            <button
              className="button button--text"
              type="button"
              onClick={() => setCheckoutNotice(null)}
            >
              Dismiss
            </button>
          </span>
        </div>
      )}

      {sandbox && (
        <div
          className="sandbox-banner"
          role="region"
          aria-label="Sample workspace controls"
        >
          <span className="sandbox-banner__message">
            <strong>Sample workspace</strong> — nothing is saved
          </span>
          <span className="sandbox-banner__actions">
            <button
              className="button button--primary"
              type="button"
              onClick={() => exitSandbox("signup")}
            >
              Create free account
            </button>
            <button
              className="button button--quiet"
              type="button"
              onClick={() => exitSandbox("signin")}
            >
              Sign in
            </button>
            <button
              className="button button--text"
              type="button"
              onClick={() => exitSandbox("home")}
            >
              Exit sample
            </button>
          </span>
        </div>
      )}

      <main id="main-content" tabIndex={-1}>
        {MULTI_SESSION_ENABLED && view === "library" && !sandbox && (
          <TranscriptLibrary
            refreshKey={libraryRefresh}
            pendingLocalRecovery={pendingLocalRecovery}
            onNew={() => void createNewTranscript()}
            onDeleted={clearDeletedTranscriptRecovery}
            onOpenLocalRecovery={(id) => void openTranscriptById(id, "recover")}
            onOpenDeviceRecovery={() => {
              if (!pendingLocalRecovery) return;
              const recoveryMatter = {
                ...blankMatterDraft(account?.settings.defaultJurisdictionPackId),
                reference: pendingLocalRecovery.matterReference
              };
              setDeviceRecoveryMatter(recoveryMatter);
              setMatter(recoveryMatter);
              setSelectedTranscript(null);
              setView("recovery");
            }}
            onDiscardLocalRecovery={() => void discardPendingRecovery()}
            onOpen={(transcript: TranscriptSummary) =>
              void openTranscriptById(
                transcript.id,
                transcript.attention === "recovery-needed"
                  ? "recover"
                  : transcript.status === "finalised"
                    ? "review"
                    : "record"
              )
            }
          />
        )}
        {view === "recovery" && (selectedTranscript || deviceRecoveryMatter) && (
          <RecoveryWorkspace
            matter={selectedTranscript ? matter : deviceRecoveryMatter!}
            matterId={selectedTranscript?.id ?? pendingLocalRecovery?.matterId}
            previousSessionCount={selectedTranscript?.sessions.length ?? 0}
            onBack={() => {
              setDeviceRecoveryMatter(null);
              navigate("library");
            }}
            onSessionChange={updateTranscriptSession}
            onCaptureStateChange={setCaptureState}
            onRecovered={(rows, session, audioUrl) => {
              if (!selectedTranscript) {
                setDeviceRecoveryMatter(null);
                if (session.matterId) {
                  setCheckoutNotice(
                    "Recording recovered. The interruption has been marked in the transcript."
                  );
                  void openTranscriptById(session.matterId, "review", "replace");
                }
                return;
              }
              setTranscriptRows(rows);
              updateTranscriptSession({
                ...session,
                ...(audioUrl ? { audioUrl } : {})
              });
              void Promise.all([
                loadTranscript(selectedTranscript.id),
                loadCompositeTranscript(selectedTranscript.id)
              ]).then(([detail, composite]) => {
                setSelectedTranscript(detail);
                setTranscriptSession(sessionFromDetail(detail));
                setTranscriptRows(composite.rows);
                setLibraryRefresh((current) => current + 1);
              });
              setCheckoutNotice(
                "Recording recovered. The interruption has been marked in the transcript."
              );
              setView("review");
              window.history.pushState(
                {},
                "",
                `/transcripts/${encodeURIComponent(selectedTranscript.id)}/review`
              );
            }}
          />
        )}
        {(view === "live" || view === "review") && (
          <div
            className={`recording-workspace ${settingsOpen ? "has-settings" : ""}`}
          >
            <div className="recording-details-rail">
              {!settingsOpen && (
                <button
                  type="button"
                  className="details-rail-toggle"
                  onClick={() => setSettingsOpen(true)}
                  aria-expanded="false"
                  aria-controls="recording-details"
                  aria-label="Expand details panel"
                  title="Open details"
                >
                  <PanelLeftOpen size={18} aria-hidden="true" />
                </button>
              )}
              <div id="recording-details">
                <RecordingSettings
                  matter={matter}
                  open={settingsOpen}
                  locked={captureLocked}
                  savedAt={sandbox ? null : savedAt}
                  onChange={setMatter}
                  onClose={() => setSettingsOpen(false)}
                  sessions={selectedTranscript?.sessions}
                  sessionsReadOnly={selectedTranscript?.status === "finalised"}
                  onSessionLabelChange={(recordingId, label) => {
                    if (!selectedTranscript) return;
                    void fetch(apiUrl(`/api/recordings/${encodeURIComponent(recordingId)}`), {
                      method: "PATCH",
                      credentials: "same-origin",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ label: label.trim() || null })
                    })
                      .then(async (response) => {
                        if (!response.ok) {
                          throw await responseError(response, "Session label could not be saved.");
                        }
                        return loadTranscript(selectedTranscript.id);
                      })
                      .then(setSelectedTranscript)
                      .catch((cause: unknown) =>
                        setCheckoutNotice(
                          cause instanceof Error ? cause.message : "Session label could not be saved."
                        )
                      );
                  }}
                />
              </div>
            </div>
            <section className="recording-canvas">
              {selectedTranscript && (
                <div className="workbench-breadcrumb">
                  <button type="button" onClick={() => navigate("library")} disabled={captureLocked}>
                    Transcripts
                  </button>
                  <span aria-hidden="true">/</span>
                  <span>{matter.title || "Untitled transcript"}</span>
                  <span className={`save-state save-state--${saveState}`} role="status">
                    {saveState === "saving"
                      ? "Saving"
                      : saveState === "conflict"
                        ? "Review conflict"
                        : saveState === "error"
                          ? "Retry save"
                          : "Saved"}
                  </span>
                  {view === "review" && selectedTranscript.status !== "finalised" && !captureLocked && (
                    <button className="button button--quiet" type="button" onClick={() => navigate("live")}>
                      Continue transcript
                    </button>
                  )}
                </div>
              )}
              {view === "live" ? (
                <LiveCapture
                  sandbox={sandbox}
                  matter={matter}
                  matterId={selectedTranscript?.id}
                  previousSessionCount={selectedTranscript?.sessions.length ?? 0}
                  canRecord={authority.canRecord}
                  onOpenSettings={() => setSettingsOpen(true)}
                  onTitleChange={(title) => setMatter((current) => ({ ...current, title }))}
                  onAuthorityConfirm={(nextAuthority) =>
                    setMatter((current) => ({ ...current, authority: nextAuthority }))
                  }
                  onSessionChange={updateTranscriptSession}
                  onCaptureStateChange={setCaptureState}
                  onLiveShareChange={setLiveShare}
                  onOpenReview={(rows, session, audioUrl) => {
                    setTranscriptRows(rows);
                    if (session && !sandbox) {
                      updateTranscriptSession({
                        ...session,
                        ...(audioUrl ? { audioUrl } : {})
                      });
                    }
                    if (selectedTranscript && !sandbox) {
                      void Promise.all([
                        loadTranscript(selectedTranscript.id),
                        loadCompositeTranscript(selectedTranscript.id)
                      ]).then(([detail, composite]) => {
                        setSelectedTranscript(detail);
                        setTranscriptSession(sessionFromDetail(detail));
                        setTranscriptRows(composite.rows);
                        setLibraryRefresh((current) => current + 1);
                      });
                    }
                    setView("review");
                    if (selectedTranscript) {
                      window.history.pushState(
                        {},
                        "",
                        `/transcripts/${encodeURIComponent(selectedTranscript.id)}/review`
                      );
                    }
                  }}
                />
              ) : (
                <>
                  {liveShare && (
                    <section
                      className="live-share-strip live-share-strip--review"
                      aria-label="Published live transcript"
                    >
                      <span className="live-share-strip__status">
                        <span className="live-share-strip__dot" aria-hidden="true" />
                        Public link available for up to one hour
                      </span>
                      <input
                        aria-label="Public live transcript URL"
                        value={liveShare.url}
                        readOnly
                        onFocus={(event) => event.currentTarget.select()}
                      />
                      <button
                        className="button button--quiet"
                        type="button"
                        onClick={() => void navigator.clipboard.writeText(liveShare.url)}
                      >
                        <Copy size={15} aria-hidden="true" />
                        Copy link
                      </button>
                      <button
                        className="button button--quiet"
                        type="button"
                        onClick={() => void stopLiveShare()}
                        disabled={liveShareBusy}
                      >
                        <Unlink size={15} aria-hidden="true" />
                        Stop sharing
                      </button>
                    </section>
                  )}
                  <ReviewWorkspace
                    initialRows={transcriptRows}
                    matter={matter}
                    session={transcriptSession}
                    onRowsChange={setTranscriptRows}
                    onSessionChange={updateTranscriptSession}
                    reviewerName={sandbox ? "Sample reviewer" : user}
                    onOpenExport={() => navigate("export")}
                    onTitleChange={(title) => setMatter((current) => ({ ...current, title }))}
                    compositeMatterId={selectedTranscript?.id}
                    readOnly={selectedTranscript?.status === "finalised"}
                    onReloadComposite={refreshComposite}
                    onFinalise={async () => {
                      if (!selectedTranscript) return;
                      const count = selectedTranscript.sessions.length;
                      const totalMinutes = Math.max(
                        1,
                        Math.round(
                          selectedTranscript.sessions.reduce(
                            (total, item) => total + item.durationMs,
                            0
                          ) / 60_000
                        )
                      );
                      if (
                        !window.confirm(
                          `Finalise this transcript?\n\n${count} session${
                            count === 1 ? "" : "s"
                          } · ${totalMinutes} min\n\nFinalisation is permanent. The transcript will become read-only.`
                        )
                      ) {
                        return;
                      }
                      await finaliseTranscript(selectedTranscript.id);
                      const [detail, composite] = await Promise.all([
                        loadTranscript(selectedTranscript.id),
                        loadCompositeTranscript(selectedTranscript.id)
                      ]);
                      setSelectedTranscript(detail);
                      setTranscriptSession(sessionFromDetail(detail));
                      setTranscriptRows(composite.rows);
                      setLibraryRefresh((current) => current + 1);
                    }}
                  />
                </>
              )}
            </section>
          </div>
        )}
        {view === "export" && (
          <ExportPanel
            sandbox={sandbox}
            onCreateAccount={() => exitSandbox("signup")}
            rows={transcriptRows}
            matter={matter}
            session={transcriptSession}
            matterFinalisationId={selectedTranscript?.finalisation?.id}
            reviewerName={sandbox ? "Sample reviewer" : user}
            onStartNewRecording={sandbox ? undefined : () => void createNewTranscript()}
          />
        )}
      </main>

      <CommandPalette
        open={commandOpen}
        onClose={() => setCommandOpen(false)}
        onNavigate={navigate}
      />
      {accountPanelOpen && account && (
        <AccountPanel
          account={account}
          initialTab={accountPanelTab}
          onChange={setAccount}
          onClose={() => {
            setAccountPanelOpen(false);
            setAccountPanelTab("profile");
          }}
          onSignOut={() => void signOut()}
        />
      )}
      <SpeakerNameDialog
        open={speakerNamePromptOpen}
        busy={speakerNameBusy}
        error={speakerNameError}
        onCancel={() => {
          if (speakerNameBusy) return;
          setSpeakerNamePromptOpen(false);
          setSpeakerNameError(null);
        }}
        onSave={(name) => void saveDefaultSpeakerName(name)}
      />
    </div>
  );
}

export function App() {
  const shared = window.location.pathname.match(/^\/shared\/([^/]+)$/);
  if (shared) {
    let token = shared[1];
    try {
      token = decodeURIComponent(token);
    } catch {
      // The public socket will return the same unavailable state as any
      // invalid or expired token.
    }
    return <SharedTranscriptPage token={token} />;
  }
  return <AuthenticatedApp />;
}
